import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchBlsForecast,
  centroidFromFieldGeoJson,
  BLS_PRACTICES,
  BLS_TARGETS,
  blsRiskStatus,
  worstBlsRisk,
  blsOverallFarmerAlert,
  type BlsDataset,
  type BlsForecastResult,
  type BlsRiskLevel,
  type BlsTarget,
} from '../utils/blsForecast';
import { useTheme } from '../theme/ThemeContext';
import InfoPopupDialog from './InfoPopupDialog';

const STATUS_ROTATE_MS = 2800;

const TARGET_ORDER: BlsTarget[] = ['YLI', 'YLS', 'TFL', 'Shooting'];

function statusTickerLine(target: BlsTarget, level: BlsRiskLevel): string {
  if (target === 'YLI') {
    if (level === 'low') return 'Infection looks okay — still on older leaves';
    if (level === 'watch') return 'Infection starting to move up the plant';
    if (level === 'high') return 'Infection reaching young leaves — check soon';
    return 'Infection bad on young leaves — walk the field';
  }
  if (target === 'YLS') {
    if (level === 'low') return 'Spots look okay — still on older leaves';
    if (level === 'watch') return 'Spots starting to climb the plant';
    if (level === 'high') return 'Spots reaching young leaves — check soon';
    return 'Spots bad on young leaves — walk the field';
  }
  if (target === 'TFL') {
    if (level === 'low') return 'Plenty of green working leaves';
    if (level === 'watch') return 'Green working leaves only fair';
    if (level === 'high') return 'Too few green working leaves';
    return 'Very few green working leaves left';
  }
  if (level === 'low') return 'Leaves near the bunch look healthy';
  if (level === 'watch') return 'Leaves near the bunch only fair';
  if (level === 'high') return 'Leaves near the bunch look weak';
  return 'Leaves near the bunch look very weak';
}

type Props = {
  headingClass: string;
  mutedClass: string;
  inputClass: string;
  compact?: boolean;
  fieldGeoJson?: Record<string, unknown> | null;
  mapCenter?: [number, number];
  farmId?: string | number | null;
  fieldName?: string | null;
  requireField?: boolean;
};

type InfoKey = `practice:${BlsDataset}` | `row:${BlsTarget}` | 'overall' | 'leaf';

const LEVEL_STYLE: Record<
  BlsRiskLevel,
  { fill: string; badgeBg: string; pillBg: string; pillFg: string; icon: string }
> = {
  severe: { fill: '#E8998A', badgeBg: '#3D211A', pillBg: '#F0C9AE', pillFg: '#7A3B1D', icon: '⚠️' },
  high: { fill: '#E8998A', badgeBg: '#3D211A', pillBg: '#F0C9AE', pillFg: '#7A3B1D', icon: '⚠️' },
  watch: { fill: '#E8C86A', badgeBg: '#3A3212', pillBg: '#F5E7B0', pillFg: '#7A5C10', icon: '👁️' },
  low: { fill: '#7FD996', badgeBg: '#153A2A', pillBg: '#CDEBD1', pillFg: '#1F6B3B', icon: '✓' },
};

const LEVEL_STYLE_LIGHT: Record<
  BlsRiskLevel,
  { fill: string; badgeBg: string; pillBg: string; pillFg: string; icon: string }
> = {
  severe: { fill: '#E07A6A', badgeBg: '#FCEBE6', pillBg: '#F0C9AE', pillFg: '#7A3B1D', icon: '⚠️' },
  high: { fill: '#E8998A', badgeBg: '#FCEBE6', pillBg: '#F0C9AE', pillFg: '#7A3B1D', icon: '⚠️' },
  watch: { fill: '#E0C45A', badgeBg: '#FBF6E0', pillBg: '#F5E7B0', pillFg: '#7A5C10', icon: '👁️' },
  low: { fill: '#5BC97A', badgeBg: '#E8F7EC', pillBg: '#CDEBD1', pillFg: '#1F6B3B', icon: '✓' },
};

const MARKER_COLOR: Record<BlsTarget, string> = {
  YLI: '#C45C26',
  YLS: '#B8860B',
  TFL: '#2E7D4F',
  Shooting: '#6B3FA0',
};

function fmtScore(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

type PlantMarker = {
  target: BlsTarget;
  side: 'left' | 'right';
  /** Vertical position of the label in the stage (0–100). */
  labelTopPct: number;
  /** Where the leader tip lands on the plant (0–100 of stage). */
  tipTopPct: number;
  tipLeftPct: number;
  title: string;
  value: string;
};

function PlantCallout({
  marker,
  color,
  onClick,
}: {
  marker: PlantMarker;
  color: string;
  onClick: () => void;
}) {
  const { side, labelTopPct, title, value } = marker;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className="pointer-events-auto absolute z-[3] max-w-[88px] rounded-lg px-1.5 py-1 text-left text-white shadow-lg"
      style={{
        top: `${labelTopPct}%`,
        left: side === 'left' ? '1%' : undefined,
        right: side === 'right' ? '1%' : undefined,
        transform: 'translateY(-50%)',
        background: color,
      }}
    >
      <p className="m-0 text-[9px] font-semibold leading-tight">{title}</p>
      <p className="m-0 mt-0.5 text-[11px] font-bold leading-none tabular-nums">{value}</p>
    </button>
  );
}

/** Full-screen plant view — labels stay locked to the plant (no drag/zoom). */
function PlantFullscreenLightbox({
  open,
  onClose,
  markers,
  isDark,
  onMarkerClick,
}: {
  open: boolean;
  onClose: () => void;
  markers: PlantMarker[];
  isDark: boolean;
  onMarkerClick: (t: BlsTarget) => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[220] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Banana plant full screen"
    >
      <div
        className="absolute inset-0"
        style={{
          background: isDark
            ? 'radial-gradient(ellipse at 50% 55%, #1c2418 0%, #070807 72%)'
            : 'radial-gradient(ellipse at 50% 55%, #e8efe4 0%, #cfd8c8 72%)',
        }}
        aria-hidden
      />

      <div
        className={`relative z-10 flex items-center gap-2 px-3 py-2.5 ${
          isDark ? 'text-[#e6edf3]' : 'text-[#111827]'
        }`}
      >
        <div className="min-w-0 flex-1">
          <p className="m-0 text-sm font-semibold">Plant view</p>
          <p className={`m-0 text-[11px] ${isDark ? 'text-[#9a9a96]' : 'text-[#6b7280]'}`}>
            Tap a label for detail
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={`rounded-lg p-2 ${isDark ? 'bg-white/10 hover:bg-white/15' : 'bg-black/5 hover:bg-black/10'}`}
          aria-label="Close"
        >
          <i className="ri-close-line text-xl" />
        </button>
      </div>

      {/* Stage: plant + labels share one box so tips stay correct */}
      <div className="relative z-10 mx-auto flex min-h-0 w-full max-w-3xl flex-1 items-center justify-center px-2 pb-4">
        <div className="relative h-full w-full max-h-full" style={{ aspectRatio: '576 / 1024', maxWidth: 'min(100%, 420px)' }}>
          <img
            src="/bls/banana-plant.png"
            alt="Banana plant"
            className="absolute inset-0 z-[1] mx-auto h-full w-full object-contain select-none pointer-events-none"
            style={{
              filter:
                'drop-shadow(0 28px 22px rgba(0,0,0,0.5)) drop-shadow(0 6px 8px rgba(0,0,0,0.28))',
            }}
            draggable={false}
          />
          <div
            className="pointer-events-none absolute bottom-[3%] left-1/2 z-0 h-[4%] w-[55%] -translate-x-1/2 rounded-[50%]"
            style={{
              background: isDark ? 'rgba(0,0,0,0.5)' : 'rgba(40,50,30,0.22)',
              filter: 'blur(8px)',
            }}
            aria-hidden
          />

          <svg
            className="pointer-events-none absolute inset-0 z-[2] h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden
          >
            {markers.map((m) => (
              <line
                key={`line-${m.target}`}
                x1={m.side === 'left' ? 8 : 92}
                y1={m.labelTopPct}
                x2={m.tipLeftPct}
                y2={m.tipTopPct}
                stroke={MARKER_COLOR[m.target]}
                strokeWidth="0.55"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </svg>
          {markers.map((m) => (
            <span
              key={`dot-${m.target}`}
              className="pointer-events-none absolute z-[2] h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full shadow"
              style={{
                left: `${m.tipLeftPct}%`,
                top: `${m.tipTopPct}%`,
                background: MARKER_COLOR[m.target],
              }}
              aria-hidden
            />
          ))}
          {markers.map((m) => (
            <button
              key={m.target}
              type="button"
              onClick={() => onMarkerClick(m.target)}
              className="absolute z-[4] max-w-[110px] rounded-lg px-2 py-1.5 text-left text-white shadow-lg"
              style={{
                top: `${m.labelTopPct}%`,
                left: m.side === 'left' ? '0%' : undefined,
                right: m.side === 'right' ? '0%' : undefined,
                transform: 'translateY(-50%)',
                background: MARKER_COLOR[m.target],
              }}
            >
              <p className="m-0 text-[11px] font-semibold leading-tight">{m.title}</p>
              <p className="m-0 mt-0.5 text-sm font-bold leading-none tabular-nums">{m.value}</p>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

function formatWeek(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatForecastDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function outlookDateIso(daysAhead = 5): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + daysAhead);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dayNum = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dayNum}`;
}

function InfoDot({
  open,
  label,
  onClick,
  isDark,
}: {
  open: boolean;
  label: string;
  onClick: () => void;
  isDark: boolean;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-expanded={open}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={`shrink-0 h-5 w-5 rounded-full inline-flex items-center justify-center ${
        isDark
          ? `text-[#7FD996] hover:bg-white/10 ${open ? 'bg-white/15' : ''}`
          : `text-[#008540] hover:bg-[#00854014] ${open ? 'bg-[#00854026]' : ''}`
      }`}
    >
      <i className="ri-information-line text-[13px] leading-none" aria-hidden />
    </button>
  );
}

function resolveFieldPoint(
  fieldGeoJson?: Record<string, unknown> | null,
  mapCenter?: [number, number]
): { lat: number; lon: number } | null {
  const fromField = centroidFromFieldGeoJson(fieldGeoJson);
  if (fromField) return fromField;
  if (mapCenter && mapCenter.length >= 2) {
    const lon = Number(mapCenter[0]);
    const lat = Number(mapCenter[1]);
    if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon };
  }
  return null;
}

export default function BlsForecastCard({
  headingClass,
  mutedClass,
  compact,
  fieldGeoJson = null,
  mapCenter,
  farmId = null,
  fieldName = null,
  requireField = false,
}: Props) {
  const { isDark } = useTheme();
  const [practice, setPractice] = useState<BlsDataset | ''>('');
  const [infoOpen, setInfoOpen] = useState<InfoKey | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<BlsForecastResult[] | null>(null);
  const [plantOpen, setPlantOpen] = useState(false);
  const [statusIdx, setStatusIdx] = useState(0);
  const [statusVisible, setStatusVisible] = useState(true);
  const [reduceMotion, setReduceMotion] = useState(false);

  const toggleInfo = (key: InfoKey) => {
    setInfoOpen((cur) => (cur === key ? null : key));
  };

  const farmIdStr = farmId != null && String(farmId).trim() ? String(farmId) : undefined;
  const fieldPoint = centroidFromFieldGeoJson(fieldGeoJson);
  const hasFieldLocation = Boolean(fieldPoint || farmIdStr);
  const displayName = (fieldName && String(fieldName).trim()) || (farmIdStr ? `Field ${farmIdStr}` : null);
  const title = displayName ? `Black sigatoka — ${displayName}` : 'Black sigatoka';
  const levelStyle = isDark ? LEVEL_STYLE : LEVEL_STYLE_LIGHT;

  const run = async () => {
    setLoading(true);
    setError(null);
    setInfoOpen(null);
    try {
      const pt = resolveFieldPoint(fieldGeoJson, requireField ? undefined : mapCenter);
      if (requireField && !pt && !farmIdStr) {
        throw new Error('Select a banana field first so we can use its rain, temperature, and humidity.');
      }
      if (!pt && !farmIdStr) {
        throw new Error('Select a field first so we can use its weather.');
      }
      if (!practice) {
        throw new Error('Select how you care for this field so we can use the matching outlook.');
      }
      const loc = {
        ...(pt ? { lat: pt.lat, lon: pt.lon } : {}),
        ...(farmIdStr ? { farm_id: farmIdStr } : {}),
      };
      const settled = await Promise.allSettled(
        BLS_TARGETS.map((t) =>
          fetchBlsForecast({
            dataset: practice,
            target: t.value,
            horizon_weeks: 1,
            n_mc: 32,
            ...loc,
          })
        )
      );
      const ok = settled
        .filter((s): s is PromiseFulfilledResult<BlsForecastResult> => s.status === 'fulfilled')
        .map((s) => s.value);
      if (ok.length === 0) {
        const first = settled.find((s) => s.status === 'rejected') as PromiseRejectedResult | undefined;
        throw first?.reason instanceof Error ? first.reason : new Error('Could not load the 1-week outlook.');
      }
      setRows(ok);
      if (ok.length < BLS_TARGETS.length) {
        setError(`Loaded ${ok.length} of 4 leaf scores. Try again if a row is missing.`);
      }
    } catch (e) {
      setRows(null);
      setError(e instanceof Error ? e.message : 'Could not load the 1-week outlook.');
    } finally {
      setLoading(false);
    }
  };

  const text = compact ? 'text-[10px] sm:text-[11px]' : 'text-[11px] sm:text-xs';
  const tiny = compact ? 'text-[9px]' : 'text-[10px]';
  const statuses = (rows || []).map((r) =>
    blsRiskStatus(r.target as BlsTarget, r.forecast[0]?.yhat ?? r.last_observed.value)
  );
  const statusItems = useMemo(
    () =>
      (rows || []).map((r) => ({
        target: r.target as BlsTarget,
        level: blsRiskStatus(r.target as BlsTarget, r.forecast[0]?.yhat ?? r.last_observed.value)
          .level,
      })),
    [rows]
  );
  const overall = statuses.length ? worstBlsRisk(statuses) : null;
  const outlookIso = outlookDateIso(5);
  const nextLabel = formatWeek(outlookIso);
  const forecastDayLabel = formatForecastDay(outlookIso);
  const overallStyle = overall ? levelStyle[overall.level] : null;

  const tickerItems = useMemo(() => {
    return TARGET_ORDER.map((target) => {
      const found = statusItems.find((s) => s.target === target);
      if (!found) return null;
      return {
        target,
        level: found.level,
        line: statusTickerLine(target, found.level),
      };
    }).filter(Boolean) as { target: BlsTarget; level: BlsRiskLevel; line: string }[];
  }, [statusItems]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    setStatusIdx(0);
    setStatusVisible(true);
  }, [rows]);

  useEffect(() => {
    if (tickerItems.length < 2) return;
    let fadeTimer: number | undefined;
    if (reduceMotion) {
      const id = window.setInterval(() => {
        setStatusIdx((i) => (i + 1) % tickerItems.length);
      }, STATUS_ROTATE_MS);
      return () => window.clearInterval(id);
    }
    const id = window.setInterval(() => {
      setStatusVisible(false);
      fadeTimer = window.setTimeout(() => {
        setStatusIdx((i) => (i + 1) % tickerItems.length);
        setStatusVisible(true);
      }, 180);
    }, STATUS_ROTATE_MS);
    return () => {
      window.clearInterval(id);
      if (fadeTimer != null) window.clearTimeout(fadeTimer);
    };
  }, [tickerItems.length, reduceMotion]);

  const activeTicker = tickerItems[statusIdx] ?? tickerItems[0] ?? null;
  const activeTickerStyle = activeTicker ? levelStyle[activeTicker.level] : overallStyle;

  const scoreByTarget = useMemo(() => {
    const out: Partial<Record<BlsTarget, { now: number; next: number }>> = {};
    for (const r of rows || []) {
      const t = r.target as BlsTarget;
      const now = r.last_observed.value;
      const next = r.forecast[0]?.yhat ?? now;
      out[t] = { now, next };
    }
    return out;
  }, [rows]);

  const markers: PlantMarker[] = useMemo(
    () => [
      {
        target: 'YLI',
        side: 'left',
        labelTopPct: 18,
        tipTopPct: 14,
        tipLeftPct: 42,
        title: 'Infection',
        value: scoreByTarget.YLI ? `Leaf ${fmtScore(scoreByTarget.YLI.next)}` : '—',
      },
      {
        target: 'YLS',
        side: 'right',
        labelTopPct: 18,
        tipTopPct: 15,
        tipLeftPct: 58,
        title: 'Spots',
        value: scoreByTarget.YLS ? `Leaf ${fmtScore(scoreByTarget.YLS.next)}` : '—',
      },
      {
        target: 'TFL',
        side: 'left',
        labelTopPct: 48,
        tipTopPct: 40,
        tipLeftPct: 38,
        title: 'Working',
        value: scoreByTarget.TFL ? `${fmtScore(scoreByTarget.TFL.next)} leaves` : '—',
      },
      {
        target: 'Shooting',
        side: 'right',
        labelTopPct: 55,
        tipTopPct: 48,
        tipLeftPct: 68,
        title: 'Flowering',
        value: scoreByTarget.Shooting ? `${fmtScore(scoreByTarget.Shooting.next)} leaves` : '—',
      },
    ],
    [scoreByTarget]
  );

  const infoPopup = useMemo(() => {
    if (!infoOpen) return null;
    if (infoOpen.startsWith('practice:')) {
      const id = infoOpen.slice('practice:'.length) as BlsDataset;
      const p = BLS_PRACTICES.find((x) => x.value === id);
      if (!p) return null;
      return { title: p.label, body: p.about };
    }
    if (infoOpen === 'overall' && overall) {
      return {
        title: 'Field alert',
        body: blsOverallFarmerAlert(statusItems, overall.level, forecastDayLabel),
      };
    }
    if (infoOpen === 'leaf') {
      return {
        title: 'What the arrows mean',
        body: 'Infection = blotched top-left leaf. Spots = speckled leaf just to its right. Working = large healthy green leaf. Flowering = leaves near the bunch. Leaf ranks count from the top (leaf 1 = newest). Tap any arrow label for more detail.',
      };
    }
    if (infoOpen.startsWith('row:')) {
      const id = infoOpen.slice('row:'.length) as BlsTarget;
      const meta = BLS_TARGETS.find((x) => x.value === id);
      const sc = scoreByTarget[id];
      if (!meta) return null;
      const st = sc ? blsRiskStatus(id, sc.next) : null;
      const change =
        sc != null
          ? id === 'YLI' || id === 'YLS'
            ? ` Forecast for ${nextLabel}: leaf ${fmtScore(sc.next)}.`
            : ` Forecast for ${nextLabel}: ${fmtScore(sc.next)} leaves.`
          : '';
      return {
        title: meta.label,
        body: `${meta.about}${change} ${st ? st.hint : ''}`.replace(/\s+/g, ' ').trim(),
      };
    }
    return null;
  }, [infoOpen, overall, statusItems, forecastDayLabel, scoreByTarget, nextLabel]);

  return (
    <div className={`mt-2 sm:mt-3 pt-2 border-t ${isDark ? 'border-white/10' : 'border-black/10'}`}>
      <div className={`font-semibold mb-2 ${text} ${headingClass}`}>{title}</div>

      <p className={`block mb-1.5 text-center ${tiny} ${mutedClass}`}>How do you care for this field?</p>
      <div className="grid grid-cols-3 gap-1.5 mb-2">
        {BLS_PRACTICES.map((p) => {
          const selected = practice === p.value;
          const open = infoOpen === `practice:${p.value}`;
          return (
            <div
              key={p.value}
              className={`min-w-0 relative flex flex-col items-center rounded-xl border transition-colors ${
                selected
                  ? isDark
                    ? 'border-[#3FAE5C] bg-[#1c2432]'
                    : 'border-[#008540] bg-[#00854014]'
                  : isDark
                    ? 'border-[#2A2A26] bg-transparent hover:border-[#3FAE5C]/60'
                    : 'border-black/15 bg-white hover:border-[#008540]/60'
              }`}
            >
              <button
                type="button"
                disabled={loading}
                onClick={() => {
                  setPractice(p.value);
                  setRows(null);
                  setInfoOpen(null);
                }}
                className="w-full flex flex-col items-center gap-1 px-1 pt-2 pb-0.5 text-center bg-transparent border-0"
              >
                <span className="text-xl leading-none" aria-hidden>
                  {p.emoji}
                </span>
                <span className={`leading-tight ${compact ? 'text-[9px]' : 'text-[10px]'} ${headingClass}`}>
                  {p.shortLabel}
                </span>
              </button>
              <div className="pb-1.5">
                <InfoDot
                  open={open}
                  label={`About ${p.label}`}
                  isDark={isDark}
                  onClick={() => toggleInfo(`practice:${p.value}`)}
                />
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={run}
        disabled={loading || !practice || (requireField && !hasFieldLocation)}
        className="w-full mb-2 px-3 py-2.5 rounded-xl bg-[#2E7D4F] hover:bg-[#256641] text-white text-[13px] sm:text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5"
      >
        <span aria-hidden>{loading ? '…' : '🔎'}</span>
        {loading ? 'Checking outlook…' : 'See outlook'}
      </button>

      {error && <p className="text-[10px] text-red-600 mb-1">{error}</p>}

      {rows && overall && overallStyle && (
        <div className={`${text} ${mutedClass}`}>
          {rows[0].practice?.label && (
            <p className={`${tiny} mb-1.5 opacity-80`}>Outlook for: {rows[0].practice.label}</p>
          )}

          <p className={`mb-2 rounded-lg px-2.5 py-2 text-center font-semibold ${text} ${headingClass} ${
            isDark ? 'bg-[#1A1A17]' : 'bg-black/[0.04]'
          }`}>
            Forecast for {forecastDayLabel}
          </p>

          <div
            className="flex items-center gap-3 p-3 rounded-xl mb-2 transition-[background] duration-300 ease-out"
            style={{ background: (activeTickerStyle || overallStyle).badgeBg }}
          >
            <div
              className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-lg ${
                isDark ? 'bg-white/10' : 'bg-white/70'
              }`}
            >
              {(activeTickerStyle || overallStyle).icon}
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <p className={`m-0 ${tiny} opacity-80`}>Field status · {nextLabel}</p>
              <p
                className={`m-0 font-bold text-base sm:text-lg ${headingClass}`}
                style={{
                  opacity: statusVisible ? 1 : 0,
                  transform: statusVisible ? 'translateY(0)' : 'translateY(6px)',
                  transition: reduceMotion
                    ? 'none'
                    : 'opacity 180ms ease-out, transform 180ms ease-out',
                }}
                aria-live="polite"
              >
                {activeTicker?.line ?? overall.label}
              </p>
              <div className="mt-1.5 flex gap-1" aria-hidden>
                {tickerItems.map((item, i) => (
                  <span
                    key={item.target}
                    className="h-1 rounded-full transition-[width,opacity] duration-300 ease-out"
                    style={{
                      width: i === statusIdx ? 14 : 6,
                      opacity: i === statusIdx ? 1 : 0.35,
                      background: levelStyle[item.level].fill,
                    }}
                  />
                ))}
              </div>
            </div>
            <InfoDot
              open={infoOpen === 'overall'}
              label="Field alert"
              isDark={isDark}
              onClick={() => toggleInfo('overall')}
            />
          </div>

          <div
            className={`relative mb-2 overflow-visible rounded-xl border ${
              isDark ? 'border-[#2A2A26]' : 'border-black/10'
            }`}
            style={{
              background: isDark
                ? 'radial-gradient(ellipse at 50% 70%, #1c2418 0%, #10110f 68%)'
                : 'radial-gradient(ellipse at 50% 70%, #e8efe4 0%, #f3f4f1 68%)',
            }}
          >
            <div className="absolute top-2 right-2 z-10">
              <InfoDot
                open={infoOpen === 'leaf'}
                label="What the arrows mean"
                isDark={isDark}
                onClick={() => toggleInfo('leaf')}
              />
            </div>

            <div className="relative mx-auto w-full max-w-[340px] px-0 pt-3 pb-2">
              {/* Plant stage — tap to open larger drag-tilt view */}
              <button
                type="button"
                className="relative z-[1] mx-auto block w-[56%] max-w-[190px] cursor-zoom-in border-0 bg-transparent p-0"
                style={{ perspective: '900px' }}
                onClick={() => setPlantOpen(true)}
                aria-label="Open full-screen plant view"
                title="Tap for full-screen plant view"
              >
                <div
                  className="relative origin-bottom"
                  style={{
                    transform: 'rotateY(-7deg) rotateX(3deg)',
                    transformStyle: 'preserve-3d',
                  }}
                >
                  <img
                    src="/bls/banana-plant.png"
                    alt="Banana plant with infection, spots, working leaves, and flowering outlook"
                    className="relative z-[1] mx-auto block w-full select-none"
                    style={{
                      filter:
                        'drop-shadow(0 22px 18px rgba(0,0,0,0.45)) drop-shadow(0 4px 6px rgba(0,0,0,0.25))',
                    }}
                    draggable={false}
                  />
                </div>
                <div
                  className="pointer-events-none absolute bottom-[2%] left-1/2 z-0 h-[7%] w-[70%] -translate-x-1/2 rounded-[50%]"
                  style={{
                    background: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(40,50,30,0.22)',
                    filter: 'blur(6px)',
                  }}
                  aria-hidden
                />
                <span
                  className={`pointer-events-none absolute bottom-1 right-0 rounded-md px-1.5 py-0.5 text-[9px] font-semibold ${
                    isDark ? 'bg-black/55 text-white' : 'bg-white/85 text-[#008540]'
                  }`}
                >
                  ⤢ Full
                </span>
              </button>

              {/* Full-stage overlay: gutter labels + leader lines (no overlap / no clip) */}
              <div className="pointer-events-none absolute inset-0">
                <svg
                  className="absolute inset-0 z-[1] h-full w-full"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  {markers.map((m) => {
                    const x1 = m.side === 'left' ? 24 : 76;
                    return (
                      <line
                        key={`line-${m.target}`}
                        x1={x1}
                        y1={m.labelTopPct}
                        x2={m.tipLeftPct}
                        y2={m.tipTopPct}
                        stroke={MARKER_COLOR[m.target]}
                        strokeWidth="0.65"
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  })}
                </svg>
                {markers.map((m) => (
                  <span
                    key={`dot-${m.target}`}
                    className="absolute z-[2] h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full shadow"
                    style={{
                      left: `${m.tipLeftPct}%`,
                      top: `${m.tipTopPct}%`,
                      background: MARKER_COLOR[m.target],
                    }}
                    aria-hidden
                  />
                ))}
                {markers.map((m) => (
                  <PlantCallout
                    key={m.target}
                    marker={m}
                    color={MARKER_COLOR[m.target]}
                    onClick={() => toggleInfo(`row:${m.target}`)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <PlantFullscreenLightbox
        open={plantOpen}
        onClose={() => setPlantOpen(false)}
        markers={markers}
        isDark={isDark}
        onMarkerClick={(t) => {
          setPlantOpen(false);
          toggleInfo(`row:${t}`);
        }}
      />

      <InfoPopupDialog
        open={Boolean(infoPopup)}
        onClose={() => setInfoOpen(null)}
        title={infoPopup?.title ?? ''}
        accent="emerald"
      >
        <p className="m-0">{infoPopup?.body}</p>
      </InfoPopupDialog>
    </div>
  );
}
